import React, { useState, useEffect } from "react";
import { Col, Row, Button } from "react-bootstrap";
import { object, bool, func, number, string, oneOfType } from "prop-types";
import SearchBar from "components/SearchBar";
import Pagination from "components/Pagination";
import CandidateReportMerchandisingBanner from "components/CandidateProfileComponent/CandidateReportMerchandisingBanner";
import OtherPotentialJobs from "./OtherPotentialJobs";
import JobScoreRange from "./JobScoreRange";
import CopyCandidateConfirmationModal from "./CopyCandidateConfirmationModal";

const TalentMobility = ({
  talentMobilityData,
  candidateInfo,
  fromCandidateProfile,
  onCopyCandidateToJob,
  moveEmailCandidatesPermission,
  companyAccountId,
  pipelineStageName,
  isSampleJob,
  userData,
  onRedirectToDevelop,
  isDevelopEnabled,
}) => {
  const [selectedPotentialJob, setSelectedPotentialJob] = useState(null);
  const [potentialJobSearch, setPotentialJobSearch] = useState("");
  const [potentialJobs, setPotentialJobs] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedJobToCopyCandidate, setSelectedJobToCopyCandidate] =
    useState(null);
  const [
    showCopyCandidateConfirmationModal,
    setShowCopyCandidateConfirmationModal,
  ] = useState(false);

  useEffect(() => {
    setSelectedPotentialJob(talentMobilityData.keySeq().first());
    setPotentialJobs(talentMobilityData);
    setPotentialJobSearch("");
  }, [candidateInfo.get("testTakerId"), talentMobilityData]);

  useEffect(() => {
    const filteredBySearchPotentialJobs =
      talentMobilityData &&
      talentMobilityData.filter((job) =>
        job
          .get("jobTitle")
          .toLowerCase()
          .includes(potentialJobSearch.toLowerCase())
      );

    const firstJob = filteredBySearchPotentialJobs.keySeq().first();

    setPotentialJobs(filteredBySearchPotentialJobs);
    setSelectedPotentialJob(firstJob);
    setCurrentPage(1);
  }, [potentialJobSearch]);

  const onChangeHandler = (value) => {
    setPotentialJobSearch(value);
  };

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected + 1);
  };

  const hideModal = () => {
    setShowCopyCandidateConfirmationModal(false);
  };

  const handleSubmitFeedback = () => {
    const url = "https://forms.office.com/r/jRkAq2hf5P";

    window.open(url, "_blank", "noopener");
  };

  const otherPotentialJobsCount = potentialJobs ? potentialJobs.size : 0;

  const candidateFirstName = candidateInfo.get("testTakerFirstName");
  const possessiveRuleForName = candidateFirstName.endsWith("s") ? "'" : "'s";

  const selectedPotentialJobData =
    selectedPotentialJob &&
    potentialJobs &&
    potentialJobs.get(selectedPotentialJob);

  const sizePerPage = 10;
  const totalPaginationPages = Math.floor(
    otherPotentialJobsCount / sizePerPage + 1
  );

  const filteredByPaginationPotentialJobs =
    potentialJobs &&
    potentialJobs
      .sort((a, b) =>
        a.get("jobTitle").localeCompare(b.get("jobTitle"), undefined, {
          numeric: true,
        })
      )
      .slice((currentPage - 1) * sizePerPage, currentPage * sizePerPage);

  const pageHeaderText = `Showing ${
    currentPage === 1 ? "1" : currentPage * sizePerPage - (sizePerPage - 1)
  }-${
    currentPage * sizePerPage < otherPotentialJobsCount
      ? currentPage * sizePerPage
      : otherPotentialJobsCount
  }
      of ${otherPotentialJobsCount} Jobs`;

  const isFreeTrial = userData?.status === "free_trial";
  const isVideoInterviewEnabled = userData?.videoInterviewsEnabled === 1;
  const isAutoRenewalRep =
    userData?.rep?.am?.userId === 304 || userData?.rep?.am?.userId === 305;

  return (
    <>
      <Row className="score-report-row">
        <Col className="px-0">
          <Row style={{ marginBottom: "24px" }}>
            <Col>
              <div className="d-flex align-items-center mb-3 justify-content-between">
                <h2 className="score-detail-section-heading mb-0 pb-1">
                  Talent Mobility
                </h2>
                {!isSampleJob ? (
                  <Button
                    onClick={handleSubmitFeedback}
                    variant="outline-primary"
                    id="submitFeedbackBtn"
                    className="ml-3"
                    style={{ fontWeight: "bold" }}
                  >
                    Submit Feedback
                  </Button>
                ) : null}
              </div>
              <p className="mb-0">
                {`See active jobs in your Criteria account that ${candidateFirstName} may be a fit for based on their EPP results. For each of the following jobs, ${candidateFirstName}${possessiveRuleForName} EPP results meet the EPP score range associated with the job. If you would like to consider ${candidateFirstName} for one of these other positions, you can copy their results to the chosen job from here.`}
              </p>
            </Col>
          </Row>
          <CandidateReportMerchandisingBanner
            reportName="EPP"
            isVideoInterviewEnabled={isVideoInterviewEnabled}
            isFreeTrial={isFreeTrial}
            isAutoRenewalRep={isAutoRenewalRep}
            userAccountId={userData?.userAccountId}
            onRedirectToDevelop={onRedirectToDevelop}
            isDevelopEnabled={isDevelopEnabled}
            fromDevelopMerch
          />
          <Row>
            <Col className="pr-0">
              <h3 className="score-detail-subsection-heading">
                {`${candidateFirstName}${possessiveRuleForName} score is in range for ${otherPotentialJobsCount} jobs`}
              </h3>
              <SearchBar
                placeholder="Search Jobs"
                value={potentialJobSearch}
                onChange={onChangeHandler}
                height="40px"
              />
              {otherPotentialJobsCount > 0 && (
                <div style={{ color: "#53565a", marginTop: "12px" }}>
                  {pageHeaderText}
                </div>
              )}
              <OtherPotentialJobs
                fromCandidateProfile={fromCandidateProfile}
                jobs={filteredByPaginationPotentialJobs}
                selectedPotentialJob={selectedPotentialJob}
                setSelectedPotentialJob={setSelectedPotentialJob}
                setShowCopyCandidateConfirmationModal={
                  setShowCopyCandidateConfirmationModal
                }
                setSelectedJobToCopyCandidate={setSelectedJobToCopyCandidate}
                candidateFirstName={candidateFirstName}
                moveEmailCandidatesPermission={moveEmailCandidatesPermission}
                companyAccountId={companyAccountId}
                pipelineStageName={pipelineStageName}
              />
              {otherPotentialJobsCount > sizePerPage && (
                <Pagination
                  totalPaginationPages={totalPaginationPages}
                  handlePageChange={handlePageChange}
                  currentPage={currentPage}
                />
              )}
            </Col>
            <Col>
              {otherPotentialJobsCount > 0 && (
                <>
                  <JobScoreRange
                    job={selectedPotentialJobData}
                    jobDetailId={selectedPotentialJob}
                    fromCandidateProfile={fromCandidateProfile}
                    setShowCopyCandidateConfirmationModal={
                      setShowCopyCandidateConfirmationModal
                    }
                    setSelectedJobToCopyCandidate={
                      setSelectedJobToCopyCandidate
                    }
                    candidateFirstName={candidateFirstName}
                    moveEmailCandidatesPermission={
                      moveEmailCandidatesPermission
                    }
                  />
                  <div
                    style={{ color: "#53565a", margin: "14px 0px 0px 14px" }}
                  >
                    <strong>Tip:</strong> In order to effectively use the Talent
                    Mobility section, ensure that you are using recognizable job
                    names and applying score ranges to your jobs that contain
                    the EPP.
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <CopyCandidateConfirmationModal
        showModal={showCopyCandidateConfirmationModal}
        hideModal={hideModal}
        onCopyCandidateToJob={onCopyCandidateToJob}
        candidateInfo={candidateInfo}
        selectedJobToCopyCandidate={selectedJobToCopyCandidate}
        pipelineStageName={pipelineStageName}
      />
    </>
  );
};

TalentMobility.propTypes = {
  talentMobilityData: object,
  candidateInfo: object,
  fromCandidateProfile: bool,
  onCopyCandidateToJob: func,
  moveEmailCandidatesPermission: bool,
  companyAccountId: number,
  pipelineStageName: oneOfType([string, bool]),
  isSampleJob: bool,
  userData: object,
  onRedirectToDevelop: func,
  isDevelopEnabled: bool,
};

export default TalentMobility;
